import React, { useRef, useState, useCallback } from 'react'
import { m, useInView } from 'framer-motion'
import { ZoomIn, Clock, Coffee, Utensils, Users, CheckCircle, ChevronDown } from 'lucide-react'
import ImageModal from './ImageModal'
import { useTheme } from '../../../context/ThemeContext'
import { useLanguage } from '../../../context/LanguageContext'
import { IntroContent } from '../../../types/content.types'
import { getImageUrl } from '../../../utils/imageUtils'
import { trackEvent } from '../../../utils/analytics'

interface IntroProps {
    content: IntroContent
}

const iconMap: Record<string, React.ComponentType<any>> = {
    Clock,
    Coffee,
    Utensils,
    Users
}

const Intro: React.FC<IntroProps> = ({ content }) => {
    const ref = useRef(null)
    const isInView = useInView(ref, { once: true, margin: '-100px' })
    const [modalImage, setModalImage] = useState<{ src: string; alt: string } | null>(null)
    const { theme } = useTheme()
    const { t } = useLanguage()

    const scrollToSignature = useCallback(() => {
        trackEvent('nav_click', { section: 'signature', source: 'intro_cta' })
        const el = document.getElementById('signature') || document.getElementById('menu-detail')
        el?.scrollIntoView({ behavior: 'smooth' })
    }, [])

    const handleImageClick = useCallback(() => {
        setModalImage({ src: getImageUrl(content.imageUrl), alt: content.imageAlt })
        trackEvent('intro_image_view', { item_name: content.imageAlt })
    }, [content.imageUrl, content.imageAlt])

    const handleCloseModal = useCallback(() => {
        setModalImage(null)
    }, [])

    return (
        <section id="intro" className="restaurant-intro">
            <div className="restaurant-intro-container">
                {/* Image */}
                <m.div
                    ref={ref}
                    initial={{ opacity: 0, x: -50 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.8 }}
                    className="relative w-full"
                >
                    <button
                        onClick={handleImageClick}
                        className="restaurant-intro-image group cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-4 focus:ring-offset-black"
                    >
                        <img
                            src={getImageUrl(content.imageUrl)}
                            alt={content.imageAlt}
                            loading="lazy"
                            className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent group-hover:from-black/40 transition-all duration-500 pointer-events-none" />

                        {/* Zoom Icon */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                            <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full">
                                <ZoomIn size={28} className="text-white" />
                            </div>
                        </div>
                    </button>
                    {/* Decorative Element */}
                    <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-red-600/20 rounded-full -z-10 hidden sm:block" />
                </m.div>

                {/* Content */}
                <m.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="restaurant-intro-content"
                >
                    <div>
                        <div>
                            <span className="restaurant-intro-label">{content.label}</span>
                            <div className="h-px w-12 bg-red-600 mt-3" />
                        </div>
                        <h2 className="restaurant-intro-title">
                            {content.title}
                        </h2>
                    </div>

                    {/* 대표 인사말 */}
                    <div className="restaurant-intro-quote">
                        <p className="restaurant-intro-quote-text">
                            {content.quote}
                        </p>
                        <p className="restaurant-intro-quote-author">
                            {content.quoteAuthor}
                        </p>
                    </div>

                    {/* 주요 내용 */}
                    <div className="restaurant-intro-content-sections">
                        {content.sections.map((section, index) => (
                            <div key={index} className="restaurant-intro-section">
                                <h3 className="restaurant-intro-section-title">{section.title}</h3>
                                {section.content && (
                                    <p className="restaurant-intro-text" style={{ whiteSpace: 'pre-line' }}>
                                        {section.content}
                                    </p>
                                )}
                                {section.menuItems && (
                                    <div className="restaurant-intro-menu-list">
                                        {section.menuItems.map((item, itemIndex) => (
                                            <div key={itemIndex} className="restaurant-intro-menu-item">
                                                <CheckCircle size={16} className="restaurant-intro-menu-icon" />
                                                <div>
                                                    <strong>{item.name}</strong>
                                                    <p>{item.description}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* 하이라이트 */}
                    <div className="restaurant-intro-features">
                        {content.highlights.map((highlight, index) => {
                            const Icon = iconMap[highlight.icon] || Clock
                            return (
                                <div key={index} className="restaurant-feature-item">
                                    <div className="restaurant-feature-icon">
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <h3 className="restaurant-feature-title">{highlight.title}</h3>
                                    <p className="restaurant-feature-description">
                                        {highlight.description}
                                    </p>
                                </div>
                            )
                        })}
                    </div>

                    {/* 마무리 메시지 */}
                    <div className="restaurant-intro-closing">
                        <p className="restaurant-intro-closing-text" style={{ whiteSpace: 'pre-line' }}>
                            {content.closing}
                        </p>
                    </div>

                    {/* 마이크로 CTA — 식욕 단계(시그니처 메뉴)로 유도 */}
                    <div className="restaurant-intro-cta">
                        <button type="button" onClick={scrollToSignature} className="restaurant-intro-cta-button">
                            <span>{t('cta.viewMenu')}</span>
                            <ChevronDown size={18} />
                        </button>
                    </div>
                </m.div>
            </div>

            {/* Image Modal */}
            <ImageModal
                isOpen={!!modalImage}
                onClose={handleCloseModal}
                imageSrc={modalImage?.src || ''}
                imageAlt={modalImage?.alt || ''}
            />
        </section>
    )
}

export default React.memo(Intro)
